import React, { forwardRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { useSharedGetReviewState } from '../hooks/actions/useReviewState';
import { HoneyloveReviewSet } from '../models/HoneyloveReview.model';
import { reviewPageFamily } from '../recoil/atoms';
import { Spinner } from '../root/components/Spinner';
import { CardBody } from './CardBody';
import { CardFooter } from './CardFooter';
import { Stars } from './Stars';
import { ViewProduct } from '../models/ViewProduct.model';
import { DivButton } from '../root/components/DivButton';
import { ImageLoader } from '../product_detail/components/ImageLoader';
import styles from './ProductReviewsDesktop.module.scss';
import { Button } from '../root/components/buttons/Button';

interface Props {
  product: ViewProduct;
}

interface ReviewProps {
  name: string;
  isVerifiedBuyer: boolean;
  starRating: number;
  title: string;
  content: string;
  images: string[];
}

const MAX_REVIEW_LENGTH = 250;

const ProductReview: React.FC<ReviewProps> = ({ name, isVerifiedBuyer, starRating, title, content, images = [] }) => {
  const [expanded, setExpanded] = useState(false);

  const shortReview = (
    <span
      dangerouslySetInnerHTML={{
        __html: content,
      }}
    />
  );
  const label = expanded ? 'read less' : 'read more';
  const longReview = (
    <>
      <span
        dangerouslySetInnerHTML={{
          __html: expanded ? content : content.slice(0, MAX_REVIEW_LENGTH) + '...',
        }}
      />
      <DivButton onClick={() => setExpanded(!expanded)} style={{ display: 'inline', color: '#857165' }} label={label}>
        &nbsp;{label}
      </DivButton>
    </>
  );

  // the plus 10 here allows for a buffer for the length of the read more link
  // this prevents the button activating when there are less than 10 additional characters
  const formattedContent = content.length > MAX_REVIEW_LENGTH + 10 ? longReview : shortReview;

  return (
    <div className={styles.singleReviewContainer}>
      <div className={styles.singleReviewMetadataContainer}>
        <span className={styles.singleReviewName}>{name}</span>
        {isVerifiedBuyer && <span className={styles.singleReviewVerified}>Verified Buyer</span>}
        <div className={styles.singleReviewStarContainer}>
          <Stars amount={starRating} />
        </div>
      </div>
      <div className={styles.singleReviewContentContainer}>
        <span
          className={styles.singleReviewTitle}
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
        <span className={styles.singleReviewContent}>{formattedContent}</span>
        <div className={styles.imageContainer}>
          {images.map((img, i) => (
            <ImageLoader key={i} alt={`Customer Review ${i}`} src={img} height={150} allowClickToZoom={true} />
          ))}
        </div>
      </div>
    </div>
  );
};

const ITEMS_PER_PAGE = 5;

const pageCount = (reviewSet: HoneyloveReviewSet, itemsPerPage: number) => (reviewSet ? Math.ceil(reviewSet?.bottomline?.total_review / itemsPerPage) ?? 0 : 1);

export const ProductReviewsDesktop = forwardRef<HTMLDivElement, Props>(({ product }, ref) => {
  const [reviewPage, setReviewPage] = useRecoilState(reviewPageFamily(product.handle));
  const { reviewSet } = useSharedGetReviewState({ productId: product.internalId, page: reviewPage, itemsPerPage: ITEMS_PER_PAGE });
  const totalPages = pageCount(reviewSet, ITEMS_PER_PAGE);

  const loading = !reviewSet?.bottomline;

  return (
    <div ref={ref}>
      <CardBody>
        <div className={styles.reviewShell}>
          <div className={styles.reviewContainer}>
            <div className={styles.reviewContent}>
              {loading && (
                <div className={styles.full}>
                  <Spinner visible={true} styleOverrides={{ spinner: { position: 'unset' } }} />
                </div>
              )}
              {!loading && (
                <div>
                  <div style={{ borderBottom: '1px solid black', fontSize: '20px', paddingBottom: 5 }}>
                    <Stars amount={reviewSet.bottomline.average_score} />
                    <span style={{ marginLeft: 5, fontSize: '16px', fontWeight: 300 }}>{reviewSet.bottomline.total_review} Reviews</span>
                  </div>
                  {reviewSet.reviews?.map((r, i) => {
                    return (
                      <ProductReview
                        key={i}
                        title={r.title}
                        name={r.user.display_name}
                        content={r.content}
                        isVerifiedBuyer={r.verified_buyer}
                        starRating={r.score}
                        images={r.images_data?.map((img) => img.original_url)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: 40, display: 'flex', gap: 20 }}>
            {reviewPage !== 1 && <Button size={'small'} onClick={() => setReviewPage((prev) => prev - 1)} text="Previous" />}
            {reviewPage < totalPages && <Button size={'small'} onClick={() => setReviewPage((prev) => prev + 1)} text="Show more" />}
          </div>
        </div>
      </CardBody>
      <CardFooter />
    </div>
  );
});
